// ============================================================================
// AVATAR KNOWLEDGE BASE HOOKS
// React hooks for avatar KB document management, search, and testing
// ============================================================================

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AvatarKBDocument } from '../types';
import * as avatarKBService from '../services/avatarKBService';

// ============================================================================
// TYPES
// ============================================================================

export interface AvatarKBSearchSource {
  documentId: string;
  fileName: string;
  content: string;
  relevanceScore: number;
  chunkIndex: number;
}

export interface AvatarKBSearchResponse {
  sources: AvatarKBSearchSource[];
  answer?: string;
}

export interface AvatarKBTestResult {
  query: string;
  description: string;
  passed: boolean;
  latencyMs: number;
  sourcesFound: number;
  avgRelevance: number;
  answer: string | null;
  topSource: { fileName: string; relevanceScore: number; preview: string } | null;
  error?: string;
}

export interface AvatarKBTestResponse {
  status: 'passed' | 'partial' | 'failed' | 'no_documents';
  message?: string;
  results: AvatarKBTestResult[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    avgRelevance: number;
    avgLatencyMs: number;
    documentsIndexed: number;
  };
}

export interface AvatarKBStats {
  totalDocuments: number;
  documentsProcessed: number;
  documentsProcessing: number;
  documentsPending: number;
  documentsFailed: number;
  totalChunks: number;
  totalSizeBytes: number;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * List avatar KB documents with auto-refetch
 */
export const useAvatarKBDocuments = (
  avatarDefinitionId: string,
  options?: { refetchInterval?: number }
): UseQueryResult<AvatarKBDocument[], Error> => {
  return useQuery({
    queryKey: ['avatar-kb-documents', avatarDefinitionId],
    queryFn: async () => {
      const result = await avatarKBService.listDocuments(avatarDefinitionId);
      return result.documents;
    },
    enabled: !!avatarDefinitionId,
    refetchInterval: options?.refetchInterval
  });
};

/**
 * Get avatar KB stats
 */
export const useAvatarKBStats = (
  avatarDefinitionId: string
): UseQueryResult<AvatarKBStats, Error> => {
  return useQuery({
    queryKey: ['avatar-kb-stats', avatarDefinitionId],
    queryFn: async () => {
      const result = await avatarKBService.getStats(avatarDefinitionId);
      return {
        totalDocuments: result.totalDocuments,
        documentsProcessed: result.documentsProcessed,
        documentsProcessing: result.documentsProcessing,
        documentsPending: result.documentsPending,
        documentsFailed: result.documentsFailed,
        totalChunks: result.totalChunks,
        totalSizeBytes: result.totalSizeBytes
      };
    },
    enabled: !!avatarDefinitionId
  });
};

/**
 * Upload document to avatar KB
 */
export const useUploadAvatarKBDocument = (avatarDefinitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      metadata
    }: {
      file: File;
      metadata?: { description?: string; tags?: string[] };
    }) => {
      const result = await avatarKBService.uploadDocument(avatarDefinitionId, file, metadata);
      return result.document;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avatar-kb-documents', avatarDefinitionId] });
      queryClient.invalidateQueries({ queryKey: ['avatar-kb-stats', avatarDefinitionId] });
    }
  });
};

/**
 * Delete avatar KB document
 */
export const useDeleteAvatarKBDocument = (avatarDefinitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      avatarKBService.deleteDocument(avatarDefinitionId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avatar-kb-documents', avatarDefinitionId] });
      queryClient.invalidateQueries({ queryKey: ['avatar-kb-stats', avatarDefinitionId] });
    }
  });
};

/**
 * Search avatar KB with RAG
 */
export const useSearchAvatarKB = (avatarDefinitionId: string) => {
  return useMutation({
    mutationFn: async ({
      query,
      topK = 5,
      includeAnswer = true
    }: {
      query: string;
      topK?: number;
      includeAnswer?: boolean;
    }): Promise<AvatarKBSearchResponse> => {
      const result = await avatarKBService.searchKB(avatarDefinitionId, query, {
        topK,
        includeAnswer
      });
      return { sources: result.sources, answer: result.answer };
    }
  });
};

/**
 * Test avatar KB quality
 */
export const useTestAvatarKB = (avatarDefinitionId: string) => {
  return useMutation({
    mutationFn: async (): Promise<AvatarKBTestResponse> => {
      return await avatarKBService.testKB(avatarDefinitionId);
    }
  });
};

/**
 * Utility: format file sizes
 */
export const useFormatFileSize = () => {
  return (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
};

/**
 * Utility: validate file for avatar KB upload
 */
export const useValidateAvatarKBFile = () => {
  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
    'text/csv'
  ];

  const extensionFallback: Record<string, string> = {
    md: 'text/markdown',
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    csv: 'text/csv'
  };

  const maxSizeBytes = 50 * 1024 * 1024;

  return (file: File): { valid: boolean; error?: string } => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const effectiveType = file.type || extensionFallback[extension] || '';

    if (!allowedTypes.includes(effectiveType)) {
      return {
        valid: false,
        error: 'Unsupported file type. Please upload PDF, DOCX, TXT, MD, or CSV files.'
      };
    }
    if (file.size > maxSizeBytes) {
      return { valid: false, error: 'File size exceeds maximum of 50MB.' };
    }
    return { valid: true };
  };
};
